import type { ViewId } from 'balus-common';
import { IconButton } from './IconButton';
import { useCopyViewUrl } from './useCopyViewUrl';
import { faEllipsisH, faLink, faMessage, faUserPen, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { viewTitleBarAtom } from '../viewTitleBarAtom';
import { useAtomValue, useSetAtom } from 'jotai';
import { ThemeColor } from '@view-model/models/common/color';
import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRightClickMenuOpenState } from '@user/pages/ViewModelPage/useRightClickMenuOpenState';
import { useTranslation } from 'react-i18next';
import { isDebugMode } from '@framework/utils/WithDebugMode';
import { aiWorkingViewIdsAtom } from '@user/pages/ViewModelPage/AIModelingEffect/aiWorkingViewIdsAtom';
import { playAIModelingEffectPreview } from '@user/pages/ViewModelPage/AIModelingEffect/playAIModelingEffectPreview';

type Props = {
  readonly: boolean;
  hasMeaningPanel: boolean;
  viewId: ViewId;
  onCopyView(): void;
  onOpenPopupMenu(): void;
  onAddMeaningPanel(): void;
  onToggleMeaningPanel(): void;
  onToggleShowNodeCreatedUser(): void;
  viewThemeColor: ThemeColor;
};

export const menuButtonGap = 10;
// 右上メニュー要素の最大アイコン数 (この個数のアイコンを配置可能な幅が確保される)
export const maxIconCount = 4;

export const ViewTitleMenu: React.FC<Props> = ({
  readonly,
  hasMeaningPanel,
  viewId,
  onCopyView,
  onOpenPopupMenu,
  onAddMeaningPanel,
  onToggleMeaningPanel,
  onToggleShowNodeCreatedUser,
  viewThemeColor,
}: Props) => {
  const { t } = useTranslation();
  const handleCopyViewUrl = useCopyViewUrl(viewId);
  const { setActiveRightClickMenuIds } = useRightClickMenuOpenState();

  const {
    menu: { buttonSize, fontSize, containerWidth },
  } = useAtomValue(viewTitleBarAtom);

  const handleMeaningPanelToggle = () => {
    if (!hasMeaningPanel && !readonly) {
      onAddMeaningPanel();
    } else {
      onToggleMeaningPanel();
    }
  };

  // AIを動かさずにエフェクトを確認するためのボタン。?debug=ai-modeling-effect のときだけ出す
  const modelingEffectEnabled = isDebugMode('ai-modeling-effect');
  const setAIWorkingViewIds = useSetAtom(aiWorkingViewIdsAtom);

  const handleOpenPopupMenu = () => {
    // ViewTitleMenu のが開くときは右クリックメニューを閉じる
    setActiveRightClickMenuIds([]);
    onOpenPopupMenu();
  };

  return (
    <div
      className={classNames(
        'flex shrink-0 flex-row flex-nowrap items-center justify-end',
        viewThemeColor === ThemeColor.White ? 'text-white' : ''
      )}
      style={{
        gap: menuButtonGap,
        width: containerWidth,
      }}
    >
      {/* ※ アイコンボタンの数が増減する場合には、 maxIconCount の値も合わせて変更すること。
          次のエフェクト再生ボタンはデバッグモード限定のため maxIconCount には含めず、
          viewTitleBarAtom 側で幅だけを +1 して確保している。 */}
      {modelingEffectEnabled && (
        <IconButton
          icon={faWandMagicSparkles}
          // デバッグモードでしか出ないボタンなので、翻訳キーは増やさずここに直接書く
          tooltip="AIモデリングのエフェクトを再生"
          onClick={(event) => {
            event.stopPropagation();
            playAIModelingEffectPreview(viewId, setAIWorkingViewIds);
          }}
          fontSize={fontSize}
          buttonSize={buttonSize}
          viewThemeColor={viewThemeColor}
        />
      )}
      <IconButton
        icon={faLink}
        tooltip={t('canvas.view.copyLink.label')}
        onClick={handleCopyViewUrl}
        fontSize={fontSize}
        buttonSize={buttonSize}
        viewThemeColor={viewThemeColor}
      />
      <IconButton
        icon={faCopy}
        tooltip={t('canvas.view.copy.label')}
        onClick={onCopyView}
        fontSize={fontSize}
        buttonSize={buttonSize}
        viewThemeColor={viewThemeColor}
      />
      {!readonly ? (
        <IconButton
          icon={faMessage}
          tooltip={t('canvas.meaningPanel.title')}
          onClick={handleMeaningPanelToggle}
          fontSize={fontSize}
          buttonSize={buttonSize}
          viewThemeColor={viewThemeColor}
        />
      ) : (
        // readonly の場合、意味の構造アイコンは表示するだけ
        <div style={{ width: buttonSize, height: buttonSize, pointerEvents: 'auto' }}>
          <div className={'flex size-full items-center justify-center'}>
            <FontAwesomeIcon icon={faMessage} style={{ fontSize }} color={viewThemeColor} />
          </div>
        </div>
      )}
      {!readonly && (
        <IconButton
          icon={faUserPen}
          tooltip={t('canvas.viewSetting.showCreatedUserIcon')}
          onClick={(event) => {
            event.stopPropagation();
            onToggleShowNodeCreatedUser();
          }}
          fontSize={fontSize}
          buttonSize={buttonSize}
          viewThemeColor={viewThemeColor}
        />
      )}
      {!readonly && (
        <IconButton
          icon={faEllipsisH}
          onClick={(event) => {
            event.stopPropagation();
            handleOpenPopupMenu();
          }}
          fontSize={fontSize}
          buttonSize={buttonSize}
          viewThemeColor={viewThemeColor}
        />
      )}
    </div>
  );
};
